/**
 * ThemeLanguageToggle — Theme switcher (icon-only)
 *
 * Language selection has moved to UserDropdown for better discoverability.
 * This component now only handles light/dark/system theme.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, type Theme } from '@/stores/app-store';
import { Button } from '@/components/ui/button';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { resolveIcon } from '@/lib/icon-map';
import { cn } from '@/lib/utils';

export function ThemeLanguageToggle() {
  const { t } = useTranslation();
  const { theme, setTheme } = useAppStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { config } = useLayoutConfigContext();
  const themeOptions = config.themes;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentTheme = themeOptions.find(opt => opt.value === theme) ?? themeOptions[0]!;
  const ThemeIcon = resolveIcon(currentTheme.icon);

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(!open)}>
        <ThemeIcon className="h-4 w-4" />
      </Button>

      {open && (
        <div className="absolute right-0 top-10 z-50 rounded-md border bg-popover p-2 shadow-md">
          <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('theme.label')}
          </p>
          <div className="flex gap-0.5">
            {themeOptions.map((opt) => {
              const Icon = resolveIcon(opt.icon);
              const active = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  title={t(`theme.${opt.value}`)}
                  onClick={() => { setTheme(opt.value as Theme); setOpen(false); }}
                  className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-md transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
